import { fg } from '@confluence/feature-gating';

import { useIsLoomEntryPointsEnabled } from './useIsLoomEntryPointsEnabled.ts';
import { useIsLoomBetaEnabled } from './useIsLoomBetaEnabled.ts';
import { useLoomPermissionStatus } from './useLoomPermissionStatus.ts';
import type { LoomEntryPointVariant } from './useLoomEntryPointVariantState.tsx';
import { useIsLoomDisabled } from './useIsLoomDisabled.ts';

export const useLoomEntryPointVariant = (): LoomEntryPointVariant => {
	const isBetaEnabled = useIsLoomBetaEnabled();
	const isLoomDisabled = useIsLoomDisabled();
	const isLoomEntryPointsEnabled = useIsLoomEntryPointsEnabled();

	// This can be replaced entirely by `isLoomDisabled` once Loom Beta is cleaned up
	const skipPermsCheck =
		!isLoomEntryPointsEnabled ||
		isBetaEnabled ||
		isLoomDisabled ||
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		!fg('confluence_frontend_loom_cross_sell');

	const loomEntryPointVariant = useLoomPermissionStatus({
		// If the entry point variant has been SSRd, use that result and skip the permissions check
		skip: skipPermsCheck || Boolean(window.__SSR_LOOM_ENTRY_POINT__),
	});

	// If the entry point already exists via SSR, return that value in the hook here (no need to check anything else)
	if (window.__SSR_LOOM_ENTRY_POINT__) {
		return window.__SSR_LOOM_ENTRY_POINT__;
	}

	if (isLoomDisabled || !isLoomEntryPointsEnabled) {
		return { entryPointVariant: 'DISABLED' };
	}

	if (isBetaEnabled) {
		return { entryPointVariant: 'BETA' };
	}

	// This should be moved into useIsLoomDisabled once Loom Beta is cleaned up.
	// For now, we have to do the Beta check first because `confluence_frontend_loom_cross_sell` is
	// not enabled on Beta sites
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	if (!fg('confluence_frontend_loom_cross_sell')) {
		return { entryPointVariant: 'DISABLED' };
	}

	if (
		loomEntryPointVariant.entryPointVariant === 'CROSS_FLOW' &&
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		fg('confluence_frontend_loom_cross_flow_ads_opt_out')
	) {
		return { entryPointVariant: 'DISABLED' };
	}

	return loomEntryPointVariant;
};
